import type { Map } from 'immutable';
import Immutable from 'immutable';
import type {
    RawSceneData,
    RawVideoData,
    Version,
    VersionInfo,
    RawTemplateData,
    OpaqueVideoData
} from '../types';
import type { TemplateBundle } from '../../types';
import { migrationTo9_151 } from './migrator-to-9-151';
import { migrationTo9_152 } from './migrator-to-9-152';
import { migrationTo9_153 } from './migrator-to-9-153';
import { migrationTo9_154 } from './migrator-to-9-154';
import { migrationTo9_155 } from './migrator-to-9-155';
import { migrationTo9_156 } from './migrator-to-9-156';
import { migrationTo9_157 } from './migrator-to-9-157';
import { migrationTo9_158 } from './migrator-to-9-158';
import { migrationTo9_159 } from './migrator-to-9-159';
import { migrationTo9_160 } from './migrator-to-9-160';
import { migrationTo9_162 } from './migrator-to-9-162';
import { migrationTo9_163 } from './migrator-to-9-163';
import { migrationTo9_164 } from './migrator-to-9-164';
import { migrationTo9_165 } from './migrator-to-9-165';
import { migrationTo9_166 } from './migrator-to-9-166';
import { migrationTo9_167 } from './migrator-to-9-167';
import { migrationTo9_168 } from './migrator-to-9-168';
import { migrationTo9_169 } from './migrator-to-9-169';
import { migrationTo9_170 } from './migrator-to-9-170';
import { migrationTo9_171 } from './migrator-to-9-171';
import { migrationTo9_172 } from './migrator-to-9-172';
import { migrationTo9_173 } from './migrator-to-9-173';
import { migrationTo9_174 } from './migrator-to-9-174';
import { migrationTo9_175 } from './migrator-to-9-175';
import { migrationTo9_177 } from './migrator-to-9-177';
import { migrationTo9_178 } from './migrator-to-9-178';
import { migrationTo9_180 } from './migrator-to-9-180';
import { migrationTo9_181 } from './migrator-to-9-181';
import { migrationTo9_182 } from './migrator-to-9-182';
import { migrationTo9_183 } from './migrator-to-9-183';
import { migrationTo9_184 } from './migrator-to-9-184';
import { migrationTo9_185 } from './migrator-to-9-185';
import { migrationTo9_186 } from './migrator-to-9-186/migrator-to-9-186';
import { migrationTo9_187 } from './migrator-to-9-187';
import { migrationTo9_188 } from './migrator-to-9-188';
import { migrationTo9_189 } from './migrator-to-9-189';
import { migrationTo9_190 } from './migrator-to-9-190';
import { migrationTo9_191 } from './migrator-to-9-191';
import { migrationTo9_192 } from './migrator-to-9-192';
import { migrationTo9_193 } from './migrator-to-9-193';
import { migrationTo9_194 } from './migrator-to-9-194';
import { migrationTo9_195 } from './migrator-to-9-195';
import { migrationTo9_196 } from './migrator-to-9-196';
import { migrationTo9_197 } from './migrator-to-9-197';
import { migrationTo9_198 } from './migrator-to-9-198';
import { migrationTo9_199 } from './migrator-to-9-199';
import { migrationTo9_201 } from './migrator-to-9-201';
import { migrationTo9_202 } from './migrator-to-9-202';
import { migrationTo9_203 } from './migrator-to-9-203';
import { migrationTo9_204 } from './migrator-to-9-204';
import { migrationTo9_205 } from './migrator-to-9-205';
import { migrationTo9_206 } from './migrator-to-9-206';
import { migrationTo9_207 } from './migrator-to-9-207';
import { migrationTo9_208 } from './migrator-to-9-208';
import { migrationTo9_209 } from './migrator-to-9-209';
import type { Migration } from './types';


type MigrationOptions = {
    /** Desired version to migrate to. */
    version?: Version;
};

/**
 * Any incoming data to the migrator must always contain these properties.
 */
type RequiredMigratorData = {
    version: Version;
};

/**
 * Latest migration version.
 *
 * NOTES ON VIDEO_DATA VERSIONING
 * We version the Config Schema to track changes and to ensure we've updated
 * all video_data JSON objects in use (any potentially in the frontend as well as the database).
 * Versioning uses Semantic Versioning - please ensure to update the version when making changes
 * to the Serializer directly or any of the helpers.
 * Please add a description of any changes to CHANGELOG.
 */
export const VERSION: Version = '9.209.0';

/**
 * This is the version where "version" was added to template and scene data.
 * This value should not be updated, it is used in migration to determine when to apply versions, i.e. not before
 * reaching 9.113.0. See `updateVideoData`.
 */
const VERSIONING_INTRODUCTION: Version = '9.113.0';

/**
 * Upgrades `video_data`, `template_data`, `scene_design_data` to the latest version (`VERSION`).
 * The minimum major `video_data` version supported by this migrator is 9.61 – anything lower than that will fail.
 *
 * ```js
 *
 * // LIBS
 * import { Migrator, VERSION } from '../../src/video-data/migrations/migrator';
 *
 * const updated_video_data = Migrator.updateVideoData(video_data);
 * ```
 */
export class Migrator {
    /**
     * Parse string version into object.
     * @param version version string
     * @returns parsed version object
     */
    static _parseVersion(version: Version): VersionInfo {
        const split_version = version.split('.');
        return {
            major: parseInt(split_version[0]),
            minor: parseInt(split_version[1]),
            patch: parseInt(split_version[2]),
        };
    }

    /**
     * @returns 0 if v1 == v2, 1 if v1 > v2 and -1 if v1 < v2. Similar idea to sign(v1 - v2) if v1 and v2 were numbers.
     */
    private static _compareVersion(v1: VersionInfo, v2: VersionInfo) {
        if (v1.major === v2.major && v1.minor === v2.minor && v1.patch === v2.patch) {
            return 0;
        }

        if (v1.major > v2.major
            || (v1.major === v2.major && v1.minor > v2.minor)
            || (v1.major === v2.major && v1.minor === v2.minor && v1.patch > v2.patch)) {
            return 1;
        }

        return -1;
    }

    /**
     * Checks if the `version` passed in is ahead of the latest version (`VERSION`).
     *
     * @param currentVersion - a version that we should check.
     * @param desiredVersion - the desired version that we should migrate to.
     * @returns `true` if the version passed in is ahead of the desired version, `false` otherwise.
     */
    static isVersionAhead(currentVersion: Version, desiredVersion: Version): boolean {
        const parsedCurrentVersion = Migrator._parseVersion(currentVersion);
        const parsedDesiredVersion = Migrator._parseVersion(desiredVersion);
        return Migrator._compareVersion(parsedCurrentVersion, parsedDesiredVersion) > 0;
    }

    static updateVideoData(data: OpaqueVideoData, options?: MigrationOptions): RawVideoData {
        const migratedData = Migrator._migrate<RawVideoData>(data, (migration: Migration, data: Immutable.Map<string, any>) => migration.migrateVideo(data), options);

        // Update the template and scene data version, as they are migrated implicitly via calls from migrateVideo, and
        // they won't have their version updated to the video data version at the end of `Migrator.migrate`.
        const parsedTargetVersion = Migrator._parseVersion(VERSIONING_INTRODUCTION);
        const migratedVersion = migratedData.version;
        const parsedMigratedVersion = Migrator._parseVersion(migratedVersion);
        if (Migrator._compareVersion(parsedMigratedVersion, parsedTargetVersion) >= 0) {
            migratedData.template.version = migratedVersion;
            migratedData.slides.forEach(scene => {
                scene.version = migratedVersion;
            });
        }

        return migratedData;
    }

    /**
     * We deliberately update the passed templateBundle in place to avoid creating a new object and break external
     * references.
     */
    static updateTemplateBundle(templateBundle: TemplateBundle, options?: MigrationOptions): TemplateBundle {
        // update scene designs

        const migratedSceneDesigns = {};

        Object.keys(templateBundle.sceneDesigns).forEach(sceneDesignId => {
            const sceneDesign = templateBundle.sceneDesigns[sceneDesignId];

            migratedSceneDesigns[sceneDesignId] = {
                landscape: Migrator.updateSceneDesignData(sceneDesign.landscape, options),
                vertical: Migrator.updateSceneDesignData(sceneDesign.vertical, options),
                square: Migrator.updateSceneDesignData(sceneDesign.square, options),
            };
        });


        return {
            templateData: Migrator.updateTemplateData(templateBundle.templateData, options),
            sceneDesigns: migratedSceneDesigns,
        };
    }

    static updateTemplateData(data: OpaqueVideoData, options?: MigrationOptions): RawTemplateData {
        return Migrator._migrate(data, (migration: Migration, data: Immutable.Map<string, any>) => migration.migrateTemplate(data), options);
    }

    static updateSceneData(data: OpaqueVideoData, options?: MigrationOptions): RawSceneData {
        return Migrator._migrate(data, (migration: Migration, data: Immutable.Map<string, any>) => migration.migrateScene(data, false), options);
    }

    static updateSceneDesignData(data: OpaqueVideoData, options?: MigrationOptions): RawSceneData {
        return Migrator._migrate(data, (migration: Migration, data: Immutable.Map<string, any>) => migration.migrateScene(data, true), options);
    }

    static #assertVersion(version: Version | undefined) {
        if (version === undefined) {
            throw new Error('unable to migrate because missing `version`');
        }
        if (version.match(/\./g).length !== 2) {
            throw new Error(`invalid version: ${version}`);
        }
    }

    /**
     * Goes through all migrations and migrate data step by step till desired version is reached.
     * If no desired version is passed, it will migrate till latest version.
     * @internal
     */
    static _migrate<T>(
        data: OpaqueVideoData,
        doMigrationCallback: (migration: Migration, data: Immutable.Map<string, any>) => Immutable.Map<string, any>,
        options?: MigrationOptions
    ): T {
        // Ensure that the opaque data object matches the required migrator data interface.
        const currentVersion: Version = (data as RequiredMigratorData).version;

        Migrator.#assertVersion(currentVersion);

        const desiredVersion = options?.version ?? VERSION;
        if (currentVersion === desiredVersion) {
            return data as T;
        }

        // the migrator uses immutable in order to prevent object mutations from interacting
        // within a migration or between different migrations. Refer to LU-1648 for more info.
        let immutableData = Immutable.fromJS(data) as unknown as Map<string, unknown>;

        immutableData = this.#migrateImmutable(immutableData, desiredVersion, doMigrationCallback);
        return immutableData.toJS() as T;
    }

    static #migrateImmutable(
        immutableData: Map<string, unknown>,
        desiredVersion: Version,
        doMigrationCallback: (migration: Migration, data: Immutable.Map<string, any>) => Immutable.Map<string, any>
    ) {
        let currentVersion = immutableData.get('version') as Version;

        if (currentVersion === desiredVersion) {
            return immutableData;
        }
        // the migrator uses immutable in order to prevent object mutations from interacting
        // within a migration or between different migrations. Refer to LU-1648 for more info.
        // iterate through the migration definitions
        while (currentVersion !== desiredVersion) {
            // confirm that version exists in _migration_steps
            const migration = this._migration_steps[currentVersion];
            if (migration === undefined) {
                // migration not found - check if the version is actually AHEAD of this code
                if (Migrator.isVersionAhead(currentVersion, VERSION)) {
                    // it's ahead! Return data with current version
                    console.warn('video\'s version is AHEAD of current version, apply latest version');
                    immutableData = immutableData.set('version', VERSION);
                    return immutableData;
                }
                else {
                    // invalid version!
                    throw new Error(`could not find migration to upgrade from version ${currentVersion}`);
                }
            }

            // apply migration
            if (migration.migration !== null) {
                immutableData = doMigrationCallback(migration.migration, immutableData);
            }

            // update version
            currentVersion = migration.versionMigratingTo;
        }

        // update version & return the up-to-date data
        immutableData = immutableData.set('version', desiredVersion);

        return immutableData;
    }

    /**
     * Connects different versions together by telling the system what `operation` it needs to run
     * to transform a video at version A (`versionMigratingFrom`) to version B (`versionMigratingTo`).
     * @key is a video's version before migration. This is a dynamic parameter.
     */
    static _migration_steps: Record<
        string,
        {
            /**
             * action to take: null (do nothing) OR method that takes `data` and returns updated `data`.
             */
            migration: Migration;
            /**
             *  video's version to upgrade (migrate) to.
             */
            versionMigratingTo: Version;
        }> =
            {
                '9.150.0': {
                    migration: migrationTo9_151,
                    versionMigratingTo: '9.151.0',
                },
                '9.151.0': {
                    migration: migrationTo9_152,
                    versionMigratingTo: '9.152.0',
                },
                '9.152.0': {
                    migration: migrationTo9_153,
                    versionMigratingTo: '9.153.0',
                },
                '9.153.0': {
                    migration: migrationTo9_154,
                    versionMigratingTo: '9.154.0',
                },
                '9.154.0': {
                    migration: migrationTo9_155,
                    versionMigratingTo: '9.155.0',
                },
                '9.155.0': {
                    migration: migrationTo9_156,
                    versionMigratingTo: '9.156.0',
                },
                '9.156.0': {
                    migration: migrationTo9_157,
                    versionMigratingTo: '9.157.0',
                },
                '9.157.0': {
                    migration: migrationTo9_158,
                    versionMigratingTo: '9.158.0',
                },
                '9.158.0': {
                    migration: migrationTo9_159,
                    versionMigratingTo: '9.159.0',
                },
                '9.159.0': {
                    migration: migrationTo9_160,
                    versionMigratingTo: '9.160.0',
                },
                '9.160.0': {
                    migration: null,
                    versionMigratingTo: '9.161.0',
                },
                '9.161.0': {
                    migration: migrationTo9_162,
                    versionMigratingTo: '9.162.0',
                },
                '9.162.0': {
                    migration: migrationTo9_163,
                    versionMigratingTo: '9.163.0',
                },
                '9.163.0': {
                    migration: migrationTo9_164,
                    versionMigratingTo: '9.164.0',
                },
                '9.164.0': {
                    migration: migrationTo9_165,
                    versionMigratingTo: '9.165.0',
                },
                '9.165.0': {
                    migration: migrationTo9_166,
                    versionMigratingTo: '9.166.0',
                },
                '9.166.0': {
                    migration: migrationTo9_167,
                    versionMigratingTo: '9.167.0',
                },
                '9.167.0': {
                    migration: migrationTo9_168,
                    versionMigratingTo: '9.168.0',
                },
                '9.168.0': {
                    migration: migrationTo9_169,
                    versionMigratingTo: '9.169.0',
                },
                '9.169.0': {
                    migration: migrationTo9_170,
                    versionMigratingTo: '9.170.0',
                },
                '9.170.0': {
                    migration: migrationTo9_171,
                    versionMigratingTo: '9.171.0',
                },
                '9.171.0': {
                    migration: migrationTo9_172,
                    versionMigratingTo: '9.172.0',
                },
                '9.172.0': {
                    migration: migrationTo9_173,
                    versionMigratingTo: '9.173.0',
                },
                '9.173.0': {
                    migration: migrationTo9_174,
                    versionMigratingTo: '9.174.0',
                },
                '9.174.0': {
                    migration: migrationTo9_175,
                    versionMigratingTo: '9.175.0',
                },
                '9.175.0': {
                    migration: null,
                    versionMigratingTo: '9.176.0',
                },
                '9.176.0': {
                    migration: migrationTo9_177,
                    versionMigratingTo: '9.177.0',
                },
                '9.177.0': {
                    migration: migrationTo9_178,
                    versionMigratingTo: '9.178.0',
                },
                '9.178.0': {
                    migration: migrationTo9_178,
                    versionMigratingTo: '9.179.0',
                },
                '9.179.0': {
                    migration: migrationTo9_180,
                    versionMigratingTo: '9.180.0',
                },
                '9.180.0': {
                    migration: migrationTo9_181,
                    versionMigratingTo: '9.181.0',
                },
                '9.181.0': {
                    migration: migrationTo9_182,
                    versionMigratingTo: '9.182.0',
                },
                '9.182.0': {
                    migration: migrationTo9_183,
                    versionMigratingTo: '9.183.0',
                },
                '9.183.0': {
                    migration: migrationTo9_184,
                    versionMigratingTo: '9.184.0',
                },
                '9.184.0': {
                    migration: migrationTo9_185,
                    versionMigratingTo: '9.185.0',
                },
                '9.185.0': {
                    migration: migrationTo9_186,
                    versionMigratingTo: '9.186.0',
                },
                '9.186.0': {
                    migration: migrationTo9_187,
                    versionMigratingTo: '9.187.0',
                },
                '9.187.0': {
                    migration: migrationTo9_188,
                    versionMigratingTo: '9.188.0',
                },
                '9.188.0': {
                    migration: migrationTo9_189,
                    versionMigratingTo: '9.189.0',
                },
                '9.189.0': {
                    migration: migrationTo9_190,
                    versionMigratingTo: '9.190.0',
                },
                '9.190.0': {
                    migration: migrationTo9_191,
                    versionMigratingTo: '9.191.0',
                },
                '9.191.0': {
                    migration: migrationTo9_192,
                    versionMigratingTo: '9.192.0',
                },
                '9.192.0': {
                    migration: migrationTo9_193,
                    versionMigratingTo: '9.193.0',
                },
                '9.193.0': {
                    migration: migrationTo9_194,
                    versionMigratingTo: '9.194.0',
                },
                '9.194.0': {
                    migration: migrationTo9_195,
                    versionMigratingTo: '9.195.0',
                },
                '9.195.0': {
                    migration: migrationTo9_196,
                    versionMigratingTo: '9.196.0',
                },
                '9.196.0': {
                    migration: migrationTo9_197,
                    versionMigratingTo: '9.197.0',
                },
                '9.197.0': {
                    migration: migrationTo9_198,
                    versionMigratingTo: '9.198.0',
                },
                '9.198.0': {
                    migration: migrationTo9_199,
                    versionMigratingTo: '9.199.0',
                },
                '9.199.0': {
                    migration: migrationTo9_197,
                    versionMigratingTo: '9.200.0',
                },
                '9.200.0': {
                    migration: migrationTo9_201,
                    versionMigratingTo: '9.201.0',
                },
                '9.201.0': {
                    migration: migrationTo9_202,
                    versionMigratingTo: '9.202.0',
                },
                '9.202.0': {
                    migration: migrationTo9_203,
                    versionMigratingTo: '9.203.0',
                },
                '9.203.0': {
                    migration: migrationTo9_204,
                    versionMigratingTo: '9.204.0',
                },
                '9.204.0': {
                    migration: migrationTo9_205,
                    versionMigratingTo: '9.205.0',
                },
                '9.205.0': {
                    migration: migrationTo9_206,
                    versionMigratingTo: '9.206.0',
                },
                '9.206.0': {
                    migration: migrationTo9_207,
                    versionMigratingTo: '9.207.0',
                },
                '9.207.0': {
                    migration: migrationTo9_208,
                    versionMigratingTo: '9.208.0',
                },
                '9.208.0': {
                    migration: migrationTo9_209,
                    versionMigratingTo: '9.209.0',
                },
            };
}
